import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, getGoogleMapsApiKey, getGoogleMapsMapId, getMapColorScheme } from '../../utils/googleMapsLoader';

interface GoogleMapProps {
  latitude: number;
  longitude: number;
  title?: string;
  zoom?: number;
  height?: string;
  className?: string;
}

export default function GoogleMap({
  latitude,
  longitude,
  title = 'Property Location',
  zoom = 15,
  height = '360px',
  className = '',
}: GoogleMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!getGoogleMapsApiKey()) {
      setError('Google Maps API key not configured');
      setIsLoading(false);
      return;
    }

    if (!latitude || !longitude) {
      setError('Invalid coordinates');
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function initMap() {
      try {
        await loadGoogleMaps();

        if (!isMounted || !mapRef.current) return;

        const position = { lat: latitude, lng: longitude };

        // Create the map
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: position,
          zoom,
          // Required for AdvancedMarkerElement. With a Map ID set, map styling is
          // controlled via cloud-based map styles (Google Cloud Console) rather
          // than the inline `styles` option, so POI-label hiding moves there.
          mapId: getGoogleMapsMapId(),
          colorScheme: getMapColorScheme(),
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });

        // Create the marker (default pin content; AdvancedMarkerElement has no
        // built-in DROP animation — omitted).
        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position,
          map: mapInstanceRef.current,
          title,
        });

        // Add info window
        const infoWindow = new google.maps.InfoWindow({
          content: `<div style="padding: 8px; font-family: sans-serif;">
            <strong style="color: #004D40;">${title}</strong>
          </div>`,
        });

        markerRef.current.addListener('gmp-click', () => {
          // Slim Maps typings (vite-env.d.ts) only declare the positional
          // open(map, anchor); AdvancedMarkerElement is a valid anchor at runtime.
          infoWindow.open(mapInstanceRef.current ?? undefined, markerRef.current as unknown as google.maps.Marker);
        });

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        if (isMounted) {
          setError('Failed to load map');
          setIsLoading(false);
        }
      }
    }

    initMap();

    return () => {
      isMounted = false;
      if (markerRef.current) {
        markerRef.current.map = null;
      }
    };
  }, [latitude, longitude, title, zoom]);

  // Update marker position when coordinates change
  useEffect(() => {
    if (mapInstanceRef.current && markerRef.current && latitude && longitude) {
      const position = { lat: latitude, lng: longitude };
      mapInstanceRef.current.setCenter(position);
      markerRef.current.position = position;
    }
  }, [latitude, longitude]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-slate-100 rounded-2xl ${className}`}
        style={{ height }}
      >
        <div className="text-center text-slate-500">
          <i className="ri-map-pin-line text-4xl mb-2"></i>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-slate-200 ${className}`} style={{ height, width: '100%' }}>
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10"
        >
          <div className="text-center text-slate-500">
            <i className="ri-loader-4-line text-3xl animate-spin mb-2"></i>
            <p className="text-sm">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}
